import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, Sparkles } from "lucide-react";
import { WEEKEND_OFFER, isWeekendOfferActive } from "../../../lib/weekendOffer";
import SampleDocumentModal from "../../statedocuments/components/SampleDocumentModal";
import "./PropertyDocumentCard.css";

// ── 24-hour clock icon ───────────────────────────────────────────────────────
const Icon24h = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
    <circle cx="16" cy="16" r="16" fill="#153568" />
    <path
      d="M16 5 A11 11 0 1 1 5.8 11.5"
      stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"
    />
    <polygon points="3.5,8.5 6.5,13 9,8.5" fill="white" />
    <text
      x="16" y="20"
      textAnchor="middle"
      fill="white"
      fontSize="10.5"
      fontWeight="900"
      fontFamily="'DM Sans',sans-serif"
      letterSpacing="-0.4"
    >24</text>
  </svg>
);

// ── Lightning / instant icon ─────────────────────────────────────────────────
const IconLightning = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
    <circle cx="16" cy="16" r="16" fill="#153568" />
    <polygon points="18,4 9,17 15.5,17 14,28 23,15 16.5,15" fill="#FFC247" />
  </svg>
);

// ── Delivery pill: icon anchored top-right, text slides out from it ──────────
function DeliveryBadge({ icon: Icon, label }) {
  return (
    <div className="pdc-pill pointer-events-none absolute right-1 top-1 z-[5] flex items-center justify-end">
      <div className="pdc-pill-text-wrap overflow-hidden">
        <span className="pdc-pill-text block whitespace-nowrap text-[10px] font-extrabold uppercase tracking-[0.06em] text-[#153568] pr-2 pl-3">
          {label}
        </span>
      </div>
      <span className="pdc-icon-zoom relative z-[1] flex-shrink-0 drop-shadow-md">
        <Icon />
      </span>

      <style>{`
        /* Default: just the logo — the yellow pill background is hidden. */
        .pdc-pill {
          background: transparent;
          border-radius: 999px;
          border: 1px solid transparent;
          box-shadow: none;
          padding: 3px 3px 3px 0;
          overflow: hidden;
          transition: background .3s ease, box-shadow .3s ease, border-color .3s ease;
        }
        /* On card hover, the pill fades in and the label slides out. */
        .group:hover .pdc-pill,
        .group:focus-visible .pdc-pill {
          background: linear-gradient(90deg, #FFC247, #FFD166);
          border-color: rgba(28,75,147,0.3);
          box-shadow: 0 4px 14px rgba(28,75,147,0.3);
        }
        .pdc-pill-text-wrap {
          max-width: 0;
          opacity: 0;
          transition: max-width .35s ease, opacity .3s ease;
          color: #153568;
        }
        .group:hover .pdc-pill-text-wrap,
        .group:focus-visible .pdc-pill-text-wrap {
          max-width: 130px;
          opacity: 1;
        }
        /* The logo gently zooms in and out. */
        .pdc-icon-zoom {
          display: inline-block;
          animation: pdc-zoom 2.4s ease-in-out infinite;
        }
        @keyframes pdc-zoom {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pdc-icon-zoom { animation: none; }
        }
      `}</style>
    </div>
  );
}

// `asLink`: render the title as a real <a href> (same navigation on click) so
// crawlers can follow it — used where the card replaces a block of links,
// e.g. "Related property documents".
export default function PropertyDocumentCard({ document, category, stateId, asLink = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sampleOpen, setSampleOpen] = useState(false);

  // Prefer an explicit stateId (e.g. when rendered on the homepage where the
  // URL has no state slug); fall back to the first path segment otherwise.
  const stateSlug =
    stateId || location.pathname.split("/").filter(Boolean)[0] || "";

  const handleClick = () => {
    // Mark the "Documents" section active so the top-nav stays highlighted even
    // when navigating into a document from the homepage (a top-level slug page).
    try {
      sessionStorage.setItem("activeNav", "property_docs");
    } catch (e) {
      /* sessionStorage unavailable, highlight just won't persist */
    }
    navigate(`/${stateSlug}/${document.id}`, {
      state: {
        category,
        stateId:      stateSlug,
        documentId:   document.id,
        documentName: document.name,
        metadata:     document.metadata,
        // Where "back" should land, the states list, not the homepage.
        backTo: "/states",
      },
    });
  };

  const nativeTitle  = document?.nativeName;
  const englishTitle = document?.name;

  return (
    <div
      className="pdc-card group relative flex h-full min-h-0 w-full cursor-pointer flex-col overflow-hidden rounded-[18px] border border-slate-200/55 bg-white font-normal not-italic shadow-[0_10px_26px_-20px_rgba(15,23,42,0.35)] ring-1 ring-[#153568]/4 transition-all duration-300 hover:-translate-y-[2px] hover:border-slate-200 hover:ring-[#153568]/10 hover:shadow-[0_22px_44px_-24px_rgba(21,53,104,0.22)]"
      onClick={() => handleClick(category)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(category);
        }
      }}
    >

      {/* ── Free diagonal ribbon: top-left corner (document JSON `free: true`) ── */}
      {document.free && (
        <div className="pointer-events-none absolute left-0 top-0 z-[6] h-[96px] w-[96px] overflow-hidden">
          <div className="absolute -left-[28px] top-[22px] w-[136px] rotate-[-45deg] bg-gradient-to-r from-emerald-500 to-emerald-400 py-[6px] text-center shadow-[0_3px_10px_rgba(5,150,105,0.5)]">
            <span className="inline-flex items-center justify-center gap-[5px] text-[13px] font-extrabold uppercase tracking-[0.1em] text-white leading-none">
              <svg width="11" height="11" viewBox="0 0 12 12" fill="white" aria-hidden>
                <path d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z"/>
              </svg>
              FREE
            </span>
          </div>
        </div>
      )}

      {/* ── Delivery badge: top-right overlaying image, text slides out ── */}
      {document.hours && (
        <DeliveryBadge icon={Icon24h} label="Get in 24 Hours" />
      )}
      {document.instant && (
        <DeliveryBadge icon={IconLightning} label="Instant Download" />
      )}

      {/* ── Document image ── */}
      <div className="pdc-media relative h-56 w-full shrink-0 overflow-hidden">
        <img
          src={document.logo?.logo1}
          alt={document?.name}
          className="h-full w-full object-cover object-top transition duration-500 ease-out group-hover:scale-[1.03]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/16 via-white/6 to-transparent" />
        {/* Small "Sample" chip, bottom-left — top-right is the delivery badge.
            Opens the sample in place; the rest of the card still navigates. */}
        {document.sample?.url && (
          <button
            type="button"
            aria-label={`View sample ${document?.name || "document"}`}
            onClick={(e) => {
              e.stopPropagation();
              setSampleOpen(true);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="absolute bottom-2 left-2 z-[5] inline-flex cursor-pointer items-center gap-1 rounded-full border border-white bg-[#FFC247] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-[#153568] shadow-[0_4px_12px_-4px_rgba(13,31,53,0.5)] transition hover:bg-[#FFD166] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#153568] md:text-[11.5px]"
          >
            <Eye className="h-3.5 w-3.5" strokeWidth={2.5} />
            Sample
          </button>
        )}
      </div>

      {/* ── Card body ── */}
      <div className="pdc-body flex min-h-0 flex-1 flex-col px-5 pb-5 pt-4 text-center font-normal not-italic">
        <h2 className="pdc-title mx-auto line-clamp-2 max-w-[95%] pb-[3px] text-[18px] font-semibold not-italic leading-[1.55] tracking-[0.005em] text-primary md:text-[20px]">
          {asLink ? (
            <a
              href={`/${stateSlug}/${document.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClick(category);
              }}
              tabIndex={-1}
              className="text-inherit no-underline"
            >
              {nativeTitle || englishTitle}
            </a>
          ) : (
            nativeTitle || englishTitle
          )}
        </h2>
        {nativeTitle && englishTitle && (
          <p className="pdc-subtitle mx-auto mt-2 line-clamp-2 max-w-[95%] pb-[2px] text-sm not-italic leading-[1.6] text-grey">
            {englishTitle}
          </p>
        )}

        {document.free && isWeekendOfferActive() && (
          <div className="mt-auto flex items-center justify-center pt-3">
            <span className="pdc-offer inline-flex items-center gap-1.5 rounded-full border border-[#153568]/20 bg-[#153568]/[0.06] px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#153568]">
              <Sparkles className="h-3 w-3" strokeWidth={2.5} />
              {WEEKEND_OFFER.label} · {WEEKEND_OFFER.dates}
            </span>
          </div>
        )}
      </div>

      {/* React events bubble out of the modal's portal into this card, so stop
          them here or every click inside the sample would open the document. */}
      {document.sample?.url && (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <SampleDocumentModal
            open={sampleOpen}
            onClose={() => setSampleOpen(false)}
            sample={document.sample}
            documentName={document?.name}
          />
        </div>
      )}
    </div>
  );
}
